import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { Observer, BehaviorSubject, Observable, throwError } from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';
import { RestUrlsConfig } from '@signature-it/ngx-generic/config';
import { CodescDTO } from '@signature-it/ngx-generic/models';
import { GetTemporaryTransactionDTO, CartTotalPriceDTO, CartTotalQuantityDTO, PointsPayment } from '@signature-it/ngx-catalogue/models';

@Injectable()
export class CartService {
    protected gcUrl = `${this.restUrlsConf.appURL}/public/cart.json`;
    protected addUrl = `${this.restUrlsConf.appURL}/public/cart/add.json`;
    protected gctpUrl = `${this.restUrlsConf.appURL}/public/cart/total_price.json`;
    protected gctqUrl = `${this.restUrlsConf.appURL}/public/cart/total_quantity.json`;
    protected caiUrl = `${this.restUrlsConf.appURL}/public/cart/clear.json`;
    protected coUrl = `${this.restUrlsConf.appURL}/public/cart/checkout.json`;
    protected requestApprovalUrl = `${this.restUrlsConf.appURL}/cart/request_approval.json`;
    protected gsmUrl = `${this.restUrlsConf.appURL}/public/cart/shipping_methods.json`;
    protected paymentConfirmationUrl = `${this.restUrlsConf.appURL}/public/cart/confirmation.json`;
    protected cgPaymentConfirmationUrl = `${this.restUrlsConf.appURL}/public/cart/cgConfirmation.json`;
    protected pointsPaymentConfirmationUrl = `${this.restUrlsConf.appURL}/public/cart/pointsConfirmation.json`;
    protected paymeCcPaymentConfirmationUrl = `${this.restUrlsConf.appURL}/public/cart/paymeCcConfirmation.json`;
    protected stripeCcPaymentConfirmationUrl = `${this.restUrlsConf.appURL}/public/cart/stripeCcConfirmation.json`;
    // protected cgPaymentConfirmationUrl = `${this.restUrlsConf.appURL}/public/cart/cgConfirmation.json`;
    protected raPaymentConfirmationUrl = `${this.restUrlsConf.appURL}/cart/raConfirmation.json`;
    protected bmPaymentConfirmationUrl = `${this.restUrlsConf.appURL}/cart/billme.json`;
    protected actPaymentConfirmationUrl = `${this.restUrlsConf.beURL}/orders.php`;
    protected couponsUsagesUrl = `${this.restUrlsConf.appURL}/public/coupon/get_coupons_usages.json`;
    protected paymentBackToCartUrl = `${this.restUrlsConf.appURL}/cart/paymentBackToCart.json`;
    protected getPointsUrl = `${this.restUrlsConf.appURL}/cart/getPoints.json`;
    protected paymetricPaymentConfirmationUrl = `${this.restUrlsConf.appURL}/public/cart/paymetricConfirmation.json`;
    protected zcreditPaymentConfirmationUrl = `${this.restUrlsConf.appURL}/public/cart/zcreditConfirmation.json`;
    protected bitPaymentConfirmationUrl = `${this.restUrlsConf.appURL}/public/cart/bitConfirmation.json`;
    protected bluesnapPaymentConfirmationUrl = `${this.restUrlsConf.appURL}/public/cart/bluesnapConfirmation.json`;

    protected route: ActivatedRoute

    totalQuantity$ = new BehaviorSubject(new CartTotalQuantityDTO());

    constructor(protected http: HttpClient,
                protected restUrlsConf: RestUrlsConfig) {
        this.route = inject(ActivatedRoute);
    }

    add(data: any) {
        return this.http.post(this.addUrl, JSON.stringify(data))
            .pipe(tap((res) => res, (error: any) => throwError(error)));
    }

    /**
     * @returns Observable<GetTemporaryTransactionDTO>
     */
    get(params = {}): Observable<GetTemporaryTransactionDTO> {
        return this.http.get(this.gcUrl, {params})
            .pipe(map((data: any) => GetTemporaryTransactionDTO.create(data)), catchError(error => throwError(error)));
    }



    /**
     * @returns Observable<CartTotalPriceDTO>
     */
    getTotalPrice(removeDelivery?, formCheckout?): Observable<CartTotalPriceDTO> {
        const queryParams = this.route.snapshot.queryParams;
        const params = {
            removeDelivery: removeDelivery ,
            formCheckout: formCheckout,
            ...queryParams
        };
        // const shippingTermsParams = shippingTerms ? `&shippingTerms= ${shippingTerms}` : '';
        return this.http.post<CartTotalPriceDTO>(this.gctpUrl, JSON.stringify(params))
            .pipe(tap((res) => res, (error: any) => throwError(error)));
    }


    /**
     * @returns Observable<CodescDTO[]|Response>
     */
    getShippingMethods(): Observable<CodescDTO[]> {
        return this.http.get<CodescDTO[]>(this.gsmUrl)
            .pipe(tap((res) => res, (error: any) => throwError(error)));
    }

    pushTotalQuantity(): void {
        this.getTotalQuantity()
            .subscribe(
                (data: CartTotalQuantityDTO) => {
                    this.totalQuantity$.next(data);
                }
            );
    }

    /**
     * @returns Observable<any|Response>
     */
    getTotalQuantity(): Observable<CartTotalQuantityDTO|Response> {
        const params = this.route.snapshot.queryParams;
        return this.http.get<CartTotalQuantityDTO|Response>(this.gctqUrl, {params})
            .pipe(tap((res) => res, (error: any) => throwError(error)));
    }

    /**
     * @returns Observable<Response>
     */
    clearAll(): Observable<boolean> {
        return this.http.post<boolean>(this.caiUrl, null)
            .pipe(tap((res) => res, (error: any) => throwError(error)));
    }

    clearItem(item: object): Observable<any> {
        const ciUrl = `${this.restUrlsConf.appURL}/public/cart/clear/${item['OrderID']}.json`;
        return this.http.post(ciUrl, JSON.stringify(item))
            .pipe(tap((res) => res, (error: any) => throwError(error)));
    }

    checkout(data: any): Observable<string> {
        return this.http.post<string>(`${this.coUrl}?serviceType=${data.serviceType}`, JSON.stringify(data.form))
            .pipe(map((res) => res), catchError(error => throwError(error)));
    }

    requestApproval(data: any): Observable<any> {
        return this.http.post(this.requestApprovalUrl, {})
            .pipe(map((res) => res), catchError(error => throwError(error)));
    }

    paymentConfirmation(params: object): Observable<any> {
        return this.http.post(this.paymentConfirmationUrl, JSON.stringify(params), {responseType: 'text'})
            .pipe(map((res: string) => res), catchError((res) =>  new Observable(
                (observer: Observer<string>) => observer.next(res.error || '')
            )));
    }

    cgPaymentConfirmation(params: object): Observable<any> {
        return this.http.post(this.cgPaymentConfirmationUrl, JSON.stringify(params), {responseType: 'text'})
            .pipe(map((res: string) => res), catchError((res) =>  new Observable(
                (observer: Observer<string>) => observer.next(res.error || '')
            )));
    }

    pointsPaymentConfirmation(params: object): Observable<any> {
        return this.http.post(this.pointsPaymentConfirmationUrl, JSON.stringify(params), {responseType: 'text'})
            .pipe(map((res: string) => res), catchError((res) =>  new Observable(
                (observer: Observer<string>) => observer.next(res.error || '')
            )));
    }

    paymeCcPaymentConfirmation(params: object): Observable<any> {
        return this.http.post(this.paymeCcPaymentConfirmationUrl, JSON.stringify(params), {responseType: 'text'})
            .pipe(map((res: string) => res), catchError((res) =>  new Observable(
                (observer: Observer<string>) => observer.next(res.error || '')
            )));
    }

    stripeCcPaymentConfirmation(params: object): Observable<any> {
        return this.http.post(this.stripeCcPaymentConfirmationUrl, JSON.stringify(params), {responseType: 'text'})
            .pipe(map((res: string) => res), catchError((res) =>  new Observable(
                (observer: Observer<string>) => observer.next(res.error || '')
            )));
    }

    billMePaymentConfirmation(params: object): Observable<any> {
        return this.http.post(this.bmPaymentConfirmationUrl, JSON.stringify(params), {responseType: 'text'})
            .pipe(map((res: string) => res), catchError((res) =>  new Observable(
                (observer: Observer<string>) => observer.next(res.error || '')
            )));
    }

    raPaymentConfirmation(params: object): Observable<any> {
        return this.http.post(this.raPaymentConfirmationUrl, JSON.stringify(params), {responseType: 'text'})
            .pipe(map((res: string) => res), catchError((res) =>  new Observable(
                (observer: Observer<string>) => observer.next(res.error || '')
            )));
    }

    actPaymentConfirmation(): Observable<any> {
        const url = this.actPaymentConfirmationUrl + location.search;
        return this.http.get(url)
            .pipe(map((res: string) => res), catchError((res) =>  new Observable(
                (observer: Observer<string>) => observer.next(res.error || '')
            )));
    }
    checkCoupon(params: string) {
        const checkCouponUrl = `${this.restUrlsConf.appURL}/public/coupon/check_coupon_code.json`;
        return this.http.post(checkCouponUrl,  JSON.stringify(params), {responseType: 'text'})
            .pipe(map((res: any) => res), catchError((res) =>  new Observable(
                (observer: Observer<string>) => observer.next(res.error || '')
            )));
    }
    getCouponsUsages(): Observable<any> {
        const url = this.couponsUsagesUrl;
        return this.http.get(url)
            .pipe(map((res: string) => res), catchError((res) =>  new Observable(
                (observer: Observer<string>) => observer.next(res.error || '')
            )));
    }

    removeCoupon(params: string) {
        const removeCouponUrl = `${this.restUrlsConf.appURL}/public/coupon/remove_coupon.json`;
        return this.http.post(removeCouponUrl,  JSON.stringify(params), {responseType: 'text'})
            .pipe(map((res: any) => res), catchError((res) =>  new Observable(
                (observer: Observer<string>) => observer.next(res.error || '')
            )));
    }

    paymentBackToCart(finalOrderId: number) {
        return this.http.post(this.paymentBackToCartUrl,  JSON.stringify(finalOrderId), {responseType: 'text'})
            .pipe(map((res: any) => res), catchError((res) =>  new Observable(
                (observer: Observer<string>) => observer.next(res.error || '')
            )));
    }

    getPoints(): Observable<PointsPayment> {
        return this.http.get(this.getPointsUrl)
            .pipe(map((res: any) => res), catchError((res) =>  new Observable(
                (observer: Observer<string>) => observer.next(res.error || '')
            )));
    }

    paymetricPaymentConfirmation(params: object): Observable<any> {
        return this.http.post(this.paymetricPaymentConfirmationUrl, JSON.stringify(params), {responseType: 'text'})
            .pipe(map((res: string) => res), catchError((res) =>  new Observable(
                (observer: Observer<string>) => observer.next(res.error || '')
            )));
    }

    zcreditPaymentConfirmation(params: object): Observable<any> {
        return this.http.post(this.zcreditPaymentConfirmationUrl, JSON.stringify(params), {responseType: 'text'})
            .pipe(map((res: string) => res), catchError((res) =>  new Observable(
                (observer: Observer<string>) => observer.next(res.error || '')
            )));
    }

    bitPaymentConfirmation(params: object): Observable<any> {
        return this.http.post(this.bitPaymentConfirmationUrl, JSON.stringify(params), {responseType: 'text'})
            .pipe(map((res: string) => res), catchError((res) =>  new Observable(
                (observer: Observer<string>) => observer.next(res.error || '')
            )));
    }

    bluesnapPaymentConfirmation(params: object): Observable<any> {
        return this.http.post(this.bluesnapPaymentConfirmationUrl, JSON.stringify(params), {responseType: 'text'})
            .pipe(map((res: string) => res), catchError((res) =>  new Observable(
                (observer: Observer<string>) => observer.next(res.error || '')
            )));
    }
}
